import { css, cva } from '@styled-system/css';
import { useQueryClient } from '@tanstack/react-query';
import { Select as AntdSelect, Cascader } from 'antd';
import { Crown, Hourglass, Lock, TrendingUp } from 'lucide-react';
import { type FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useCredits } from '@/features/credits/useCredits';
import { MobileCategoryDrilldown } from '@/features/productSearch/components/MobileCategoryDrilldown';
import { type TrendFilterValues, matchesTrendFilters } from '@/features/trend/types';
import { RelaunchSearchModal } from '@/shared/components/analysis/RelaunchSearchModal';
import { AppLayout } from '@/shared/components/layout/AppLayout';
import { Button } from '@/shared/components/ui/Button';
import { FilterTag } from '@/shared/components/ui/FilterTag';
import { Message } from '@/shared/components/ui/Message';
import { SearchBarNew } from '@/shared/components/ui/SearchBarNew';
import { useCreateAnalysisFromCriteria } from '@/shared/hooks/analysis/mutations';
import {
  type SearchCriteriaInput,
  type TrendListKind,
  useGetTrendListQuery,
  useGetVintedCountriesQuery,
  useMoveAnalysisToFolderMutation,
  useMoveAnalysisToPositionMutation,
} from '@/shared/hooks/queries/generated';
import { useIsMobile } from '@/shared/hooks/useIsMobile';
import { useVintedOptions } from '@/shared/hooks/useVintedOptions';
import { localizeCountryName } from '@/shared/utils/localizeCountry';

import { TrendFilterPopover } from './TrendFilterPopover';
import { TrendNicheDetailModal } from './TrendNicheDetailModal';
import { type TrendNiche, TrendTable } from './TrendTable';

// ============================================================================
// Page Trend (changelog 2026-09-17) — deux classements nocturnes de niches :
// Top (CA/mois, run à froid du lundi) et Tendances (croissance, quotidien).
// - catalogId : nœuds NON-FEUILLES uniquement (l'arbre catalogue sans feuilles)
// - computedAt null = aucun run publié -> état « bientôt », pas de liste vide
// - essai : 3 niches + « +N autres » via total/truncated (jamais niches.length)
// - ouvrir une niche = createAnalysisFromFilters avec relaunchCriteria TEL QUEL
// ============================================================================

const toolbar = css({
  display: 'flex',
  alignItems: 'center',
  gap: '4',
  flexWrap: 'wrap',
  paddingY: '4',
});

// Même dessin que la barre d'onglets d'une analyse (AnalysisDetailPage)
const tabBar = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '1',
  padding: '2',
  bg: 'surface.subtle',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.default',
  borderRadius: 'xl',
  width: 'fit-content',
});

const tabButton = cva({
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '3',
    paddingY: '3',
    paddingX: '7',
    fontSize: 'body',
    fontFamily: 'sans',
    fontWeight: 'medium',
    borderRadius: 'lg',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'transparent',
    background: 'transparent',
    color: 'text.secondary',
    cursor: 'pointer',
    transition: 'background 120ms ease, color 120ms ease, box-shadow 120ms ease',
    whiteSpace: 'nowrap',
  },
  variants: {
    active: {
      true: {
        bg: 'surface.default',
        color: 'text.primary',
        fontWeight: 'semibold',
        borderColor: 'border.default',
        boxShadow: 'xs',
      },
      false: {},
    },
  },
});

const metaLine = css({ fontSize: 'caption', color: 'text.tertiary', marginLeft: 'auto' });

const grid = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '2',
  paddingBottom: '10',
});

const skeletonCard = css({
  height: '52px',
  borderRadius: 'xl',
  bg: 'surface.subtle',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.subtle',
  animation: 'skeletonPulse 1.6s ease-in-out infinite',
});

const emptyState = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '4',
  paddingY: '20',
  textAlign: 'center',
  color: 'text.tertiary',
});

const upsellCard = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '4',
  padding: '6',
  borderRadius: 'xl',
  borderWidth: '1px',
  borderStyle: 'dashed',
  borderColor: 'brand.primary/40',
  bg: 'brand.primarySoft',
  textAlign: 'center',
});

// Paywall essai gratuit : la page entière est remplacée par ce bloc
const paywallCard = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '5',
  marginTop: '10',
  paddingY: '16',
  paddingX: '8',
  borderRadius: 'xl',
  borderWidth: '1px',
  borderStyle: 'dashed',
  borderColor: 'brand.primary/40',
  bg: 'brand.primarySoft',
  textAlign: 'center',
});

const paywallIcon = css({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '56px',
  height: '56px',
  borderRadius: 'full',
  bg: 'surface.default',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.default',
  color: 'brand.primary',
});

export const TrendPage: FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const locale = i18n.language?.startsWith('en') ? 'en-GB' : 'fr-FR';
  const isMobile = useIsMobile();
  // Essai gratuit : les Tendances sont bloquées — message payant à la place.
  // `trialing` reste false tant que l'abonnement n'est pas chargé : on ne
  // bloque jamais un abonné payant sur un état de chargement.
  const credits = useCredits();
  const trialLocked = credits.trialing;

  const [list, setList] = useState<TrendListKind>('top');
  // Chemin Cascader (ex. [racine, niveau1]) — le nœud interrogé est le DERNIER ;
  // null = toutes catégories (catalogId nul côté API), le défaut
  const [catalogPath, setCatalogPath] = useState<string[] | null>(null);
  // ⚠️ getTrendList n'accepte qu'UN pays (countryId: Int) — pas de multi-sélection
  const [countryId, setCountryId] = useState<number | null>(null);
  const [searchText, setSearchText] = useState('');
  // Seuils locaux (même popover que les niches d'une analyse) : masquent des
  // lignes du classement, ne changent ni les chiffres ni les rangs
  const [filters, setFilters] = useState<TrendFilterValues>({});

  const removeFilter = (...keys: (keyof TrendFilterValues)[]) => {
    setFilters((prev) => {
      const newFilters = { ...prev };
      keys.forEach((key) => delete newFilters[key]);
      return newFilters;
    });
  };

  // ─── Sélecteur catalogue : l'arbre SANS ses feuilles (contrat getTrendList),
  // aplati en « Racine » + « Racine › Niveau 1 » — sélection simple.
  const { categories } = useVintedOptions();
  // Le même arbre que la config recherche, ÉLAGUÉ de ses feuilles : seuls les
  // nœuds racine/niveau 1 non-feuilles sont sélectionnables (contrat getTrendList)
  const catalogOptions = useMemo(
    () =>
      categories
        .filter((root) => root.children?.length)
        .map((root) => ({
          value: root.value,
          label: root.label,
          children: (root.children ?? [])
            .filter((level1) => level1.children?.length)
            .map((level1) => ({ value: level1.value, label: level1.label })),
        })),
    [categories],
  );

  // Dernier nœud du chemin ; aucun chemin = toutes catégories
  const effectiveCatalogId = catalogPath?.length ? catalogPath[catalogPath.length - 1] : null;

  const { data: countriesData } = useGetVintedCountriesQuery(undefined, {
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
  });
  // Groupes par région, comme la section « Pays du vendeur » de la config
  const countryOptions = useMemo(() => {
    const countries = countriesData?.getVintedCountries ?? [];
    const order = ['europeWest', 'europeEast', 'unitedKingdom', 'northAmerica'] as const;
    return order
      .map((region) => ({
        label: t(`sellers.regions.${region}`),
        options: countries
          .filter((country) => country.region === region)
          .map((country) => ({ value: country.id, label: localizeCountryName(country.isoCode, country.name) })),
      }))
      .filter((group) => group.options.length > 0);
  }, [countriesData, t]);

  const { data, isLoading, error, refetch, isFetching } = useGetTrendListQuery(
    {
      catalogId: effectiveCatalogId != null ? Number(effectiveCatalogId) : null,
      countryId,
      list,
      // Contrat : SEULS 0 et 1 sont acceptés — null faisait échouer la query
      runOffset: 0,
    },
    {
      // Pas de requête pour un compte en essai : la page est un paywall
      enabled: !trialLocked,
      // Classements nocturnes : figés jusqu'au prochain run — cache long
      staleTime: 10 * 60 * 1000,
      // « Failed to fetch » fréquents (réseau/WAF/temps de réponse) : 3
      // tentatives avec recul progressif avant d'afficher l'erreur
      retry: 3,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    },
  );
  const trendList = data?.getTrendList;

  // Libellé de la croissance — hebdo (top) ou signal de tendance
  const growthLabel = t(list === 'top' ? 'trend.colGrowthWeekly' : 'trend.colGrowthTrend');

  // Rang FIGÉ sur l'ordre serveur, posé AVANT le filtrage local : masquer des
  // lignes ne renumérote pas le classement
  const rankedNiches = useMemo(
    () => (trendList?.niches ?? []).map((niche, index) => ({ ...niche, rank: index + 1 })),
    [trendList],
  );
  const filteredNiches = useMemo(
    () => rankedNiches.filter((niche) => matchesTrendFilters(niche.metrics, filters)),
    [rankedNiches, filters],
  );

  // Tag « borne active » de la toolbar — même dessin que la page niches
  const rangeTag = (name: string, minKey: keyof TrendFilterValues, maxKey: keyof TrendFilterValues, unit = '') => {
    const min = filters[minKey];
    const max = filters[maxKey];
    if (min === undefined && max === undefined) return null;
    const value =
      min !== undefined && max !== undefined
        ? `${min}${unit} - ${max}${unit}`
        : min !== undefined
          ? `${min}${unit} min.`
          : `${max}${unit} max.`;
    return <FilterTag name={name} value={value} onRemove={() => removeFilter(minKey, maxKey)} />;
  };

  // ─── Ouvrir une niche : relaunchCriteria TEL QUEL (contrat §2) ───
  const [openTarget, setOpenTarget] = useState<TrendNiche | null>(null);
  // Clic de ligne : popup de détail (le bouton ▶ ouvre l'analyse directement)
  const [detailTarget, setDetailTarget] = useState<TrendNiche | null>(null);
  const { createAnalysisFromCriteria } = useCreateAnalysisFromCriteria();
  const moveToFolderMutation = useMoveAnalysisToFolderMutation();
  const moveToPositionMutation = useMoveAnalysisToPositionMutation();

  const handleConfirmOpen = async (name: string, folderId: string | null) => {
    if (!openTarget?.relaunchCriteria) return;
    const result = await createAnalysisFromCriteria({
      title: name,
      criteria: openTarget.relaunchCriteria as SearchCriteriaInput,
    });
    if (result.success && result.analysis) {
      try {
        if (folderId) {
          await moveToFolderMutation.mutateAsync({ input: { analysisId: result.analysis.id, folderId } });
        }
        await moveToPositionMutation.mutateAsync({ input: { analysisId: result.analysis.id, position: 0 } });
        await queryClient.invalidateQueries({ queryKey: ['GetUserAnalysesPaginated', 'catalog'] });
      } catch {
        // le rangement peut se refaire à la main
      }
      navigate(`/analysis/products/${result.analysis.id}`);
    } else if (!result.success) {
      throw new Error(result.error ?? t('niches.relaunchError'));
    }
  };

  // Jour seul — pas d'heure ni d'ISO brut dans la ligne méta
  const dayOnly = (value: string) => new Date(value).toLocaleDateString(locale, { day: 'numeric', month: 'short' });
  const computedAtLabel =
    trendList?.computedAt != null ? t('trend.computedAt', { date: dayOnly(trendList.computedAt) }) : null;

  const hiddenCount = trendList && trendList.truncated ? Math.max(0, trendList.total - trendList.niches.length) : 0;

  // ─── Essai gratuit : pas de classement, juste le message d'upgrade ───
  if (trialLocked) {
    return (
      <AppLayout title={t('trend.title')}>
        <div className={paywallCard}>
          <span className={paywallIcon}>
            <Lock size={24} strokeWidth={1.75} />
          </span>
          <span className={css({ fontSize: 'h3', fontWeight: 'semibold', color: 'text.primary' })}>
            {t('trend.trialLockedTitle')}
          </span>
          <span className={css({ fontSize: 'bodySm', color: 'text.secondary', lineHeight: 1.6, maxWidth: '460px' })}>
            {t('trend.trialLockedHint')}
          </span>
          <Button variant="cta" size="md" onClick={() => navigate('/subscription')}>
            {t('trend.trialLockedCta')}
          </Button>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title={t('trend.title')}>
      <div className={toolbar}>
        <div className={tabBar}>
          <button type="button" className={tabButton({ active: list === 'top' })} onClick={() => setList('top')}>
            <Crown size={15} strokeWidth={1.75} />
            {t('trend.tabTop')}
          </button>
          <button type="button" className={tabButton({ active: list === 'trend' })} onClick={() => setList('trend')}>
            <TrendingUp size={15} strokeWidth={1.75} />
            {t('trend.tabTrend')}
          </button>
        </div>

        {/* Catégories : même Cascader que la config recherche — sélection
            SIMPLE, racine ou niveau 1 uniquement (l'arbre est élagué de ses
            feuilles, contrat getTrendList) ; vide = toutes catégories */}
        {isMobile ? (
          <div className={css({ width: '100%' })}>
            {/* Même drilldown plein écran que la config recherche — contraint
                à UNE sélection : seul le dernier chemin coché est retenu */}
            <MobileCategoryDrilldown
              options={catalogOptions}
              value={catalogPath ? [catalogPath] : []}
              onChange={(paths) => setCatalogPath(paths.length ? paths[paths.length - 1] : null)}
              placeholder={t('trend.allCategories')}
            />
          </div>
        ) : (
          <Cascader
            options={catalogOptions}
            value={catalogPath ?? []}
            onChange={(value) => setCatalogPath((value as string[] | undefined)?.length ? (value as string[]) : null)}
            changeOnSelect
            allowClear
            showSearch
            expandTrigger="hover"
            placeholder={t('trend.allCategories')}
            style={{ width: 260 }}
          />
        )}

        {/* Zone : un SEUL pays possible (countryId: Int côté API) — vide = toutes zones */}
        <AntdSelect
          value={countryId}
          onChange={(value) => setCountryId(value ?? null)}
          options={countryOptions}
          showSearch
          optionFilterProp="label"
          allowClear
          placeholder={t('trend.allCountries')}
          style={{ width: isMobile ? '100%' : 200 }}
        />
        <div className={css({ flex: 1, minWidth: { base: '100%', md: '220px' } })}>
          <SearchBarNew value={searchText} onChange={setSearchText} placeholder={t('niches.searchPlaceholder')} />
        </div>

        {/* Seuils de métriques — même popover que les niches d'une analyse */}
        {!isMobile && <TrendFilterPopover filters={filters} onFilterChange={setFilters} growthLabel={growthLabel} />}

        {rangeTag(t('trend.caPerMonth'), 'caPerMonthMin', 'caPerMonthMax', ' €')}
        {rangeTag(t('niches.colSoldPct'), 'soldPctMin', 'soldPctMax', ' %')}
        {rangeTag(t('niches.colSoldPerDay'), 'salesPerDayMin', 'salesPerDayMax')}
        {rangeTag(t('niches.colAvgPrice'), 'avgPriceMin', 'avgPriceMax', ' €')}
        {rangeTag(t('niches.colTimeToSell'), 'timeToSellMin', 'timeToSellMax', 'h')}
        {rangeTag(t('trend.productCount'), 'productCountMin', 'productCountMax')}
        {rangeTag(growthLabel, 'growthMin', 'growthMax', ' %')}

        {computedAtLabel && (
          <span className={metaLine}>
            {computedAtLabel}
            {list === 'top' && trendList?.weekAnchor
              ? ` · ${t('trend.weekOf', { date: dayOnly(trendList.weekAnchor) })}`
              : ''}
          </span>
        )}
      </div>

      {error != null && trendList == null ? (
        <div className={css({ display: 'flex', flexDirection: 'column', gap: '3', alignItems: 'flex-start' })}>
          <Message type="error">
            {t('trend.loadError')}
            {error instanceof Error && error.message ? ` — ${error.message}` : ''}
          </Message>
          <Button variant="secondary" size="sm" onClick={() => void refetch()} showLoader disabled={isFetching}>
            {t('trend.retry')}
          </Button>
        </div>
      ) : null}

      {isLoading ? (
        <div className={grid}>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className={skeletonCard} style={{ animationDelay: `${i * 120}ms` }} />
          ))}
        </div>
      ) : trendList && trendList.computedAt == null ? (
        // Aucun run publié : état « bientôt », jamais une liste vide muette
        <div className={emptyState}>
          <Hourglass size={30} strokeWidth={1.25} />
          <span className={css({ fontSize: 'body', fontWeight: 'semibold', color: 'text.secondary' })}>
            {t('trend.emptyTitle')}
          </span>
          <span className={css({ fontSize: 'bodySm', maxWidth: '420px', lineHeight: 1.6 })}>
            {t('trend.emptyHint')}
          </span>
        </div>
      ) : trendList ? (
        <div className={css({ display: 'flex', flexDirection: 'column', gap: '4', paddingBottom: '10' })}>
          {/* Les seuils masquent des lignes, ils ne changent pas le classement :
              on le dit explicitement, comme sur la page niches */}
          {filteredNiches.length < rankedNiches.length && (
            <span className={css({ fontSize: 'caption', color: 'text.tertiary' })}>
              {t('niches.shownCount', { shown: filteredNiches.length, total: rankedNiches.length })}
            </span>
          )}
          <TrendTable
            niches={filteredNiches}
            list={list}
            globalFilter={searchText}
            onOpen={setOpenTarget}
            onShowDetail={setDetailTarget}
          />
          {hiddenCount > 0 && (
            <div className={upsellCard}>
              <span className={css({ fontSize: 'body', fontWeight: 'semibold', color: 'text.primary' })}>
                {t('trend.upsellTitle', { count: hiddenCount })}
              </span>
              <span className={css({ fontSize: 'caption', color: 'text.secondary', lineHeight: 1.5 })}>
                {t('trend.upsellHint')}
              </span>
              <Button variant="cta" size="md" onClick={() => navigate('/subscription')}>
                {t('trend.upsellCta')}
              </Button>
            </div>
          )}
        </div>
      ) : null}

      <TrendNicheDetailModal
        niche={detailTarget}
        growthLabel={growthLabel}
        onClose={() => setDetailTarget(null)}
        onOpenAnalysis={(niche) => {
          setDetailTarget(null);
          setOpenTarget(niche);
        }}
      />

      <RelaunchSearchModal
        isOpen={openTarget !== null}
        defaultName={openTarget?.name ?? ''}
        onClose={() => setOpenTarget(null)}
        onConfirm={handleConfirmOpen}
        warningText={t('trend.launchWarning')}
      />
    </AppLayout>
  );
};
